// frontend/src/contexts/AuthContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  authenticateWithFirebase,
  updateUserProfile as apiUpdateProfile 
} from '../services/api';
import { 
  signInWithGoogle, 
  getIdToken, 
  firebaseSignOut,
  handleEmailLinkSignIn
} from '../services/firebaseAuth';
import { auth } from '../config/firebase';
import { onAuthStateChanged } from 'firebase/auth';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [authState, setAuthState] = useState({
    isAuthenticated: false,
    user: null,
    loading: true,
    firebaseUser: null
  });

  // Check for email link sign-in on mount
  useEffect(() => {
    const checkEmailLinkSignIn = async () => {
      try {
        const result = await handleEmailLinkSignIn();
        if (result) {
        }
      } catch (error) {
        console.error('Email link sign-in error:', error);
      }
    };

    checkEmailLinkSignIn();
  }, []);

  // Listen for Firebase auth state changes - This is the main auth handler
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      
      if (firebaseUser) {
        setAuthState(prev => ({ ...prev, firebaseUser, loading: true }));
        
        try {
          // Get Firebase ID token and register/sync with backend
          const idToken = await getIdToken();
          const result = await authenticateWithFirebase(idToken);
          
          // Store any backend token if provided (optional since Firebase handles auth)
          if (result.token) {
            localStorage.setItem('token', result.token);
          }
          
          setAuthState({
            isAuthenticated: true,
            user: {
              id: result.user.google_uid,
              email: result.user.email,
              name: result.user.name,
              google_uid: result.user.google_uid || firebaseUser.uid,
              uid: firebaseUser.uid, // Always include Firebase UID
              displayName: result.user.name || firebaseUser.displayName,
              ...result.user
            },
            loading: false,
            firebaseUser
          });
          
          
        } catch (error) {
          console.error('Backend authentication failed:', error);
          // Even if backend fails, keep Firebase user authenticated
          setAuthState({
            isAuthenticated: true,
            user: {
              uid: firebaseUser.uid,
              email: firebaseUser.email,
              displayName: firebaseUser.displayName,
              name: firebaseUser.displayName
            },
            loading: false,
            firebaseUser
          });
        }
      } else {
        // User signed out
        localStorage.removeItem('token');
        setAuthState({
          isAuthenticated: false,
          user: null,
          loading: false,
          firebaseUser: null
        });
      }
    });
    
    return () => unsubscribe();
  }, []);

  // Google Sign-in - Firebase will handle the auth state change
  const loginWithFirebaseGoogle = async () => {
    try {
      setAuthState(prev => ({ ...prev, loading: true }));
      await signInWithGoogle();
      // Auth state change will be handled by onAuthStateChanged
    } catch (error) {
      setAuthState(prev => ({ ...prev, loading: false }));
      throw error;
    }
  };

  // Sign out from Firebase
  const logout = async () => {
    try {
      if (authState.firebaseUser) {
        await firebaseSignOut();
      }
      localStorage.removeItem('token');
      // Auth state change will be handled by onAuthStateChanged
    } catch (error) {
      console.error('Logout failed:', error);
      // Force local state reset if Firebase logout fails
      setAuthState({
        isAuthenticated: false,
        user: null,
        loading: false,
        firebaseUser: null
      });
    }
  };

  // Update user profile in backend
  const updateUserProfile = async (profileData) => {
    try {
      setAuthState(prev => ({ ...prev, loading: true }));
      const result = await apiUpdateProfile(profileData);
      setAuthState(prev => ({
        ...prev,
        user: { ...prev.user, ...result.user },
        loading: false
      }));
      return result;
    } catch (error) {
      setAuthState(prev => ({ ...prev, loading: false }));
      throw error;
    }
  };

  // Get current Firebase ID token (useful for API calls)
  const getFirebaseToken = async () => {
    if (authState.firebaseUser) {
      return await getIdToken();
    }
    return null;
  };

  // Show loading spinner while authentication is being determined
  if (authState.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-royal-900">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ 
      isAuthenticated: authState.isAuthenticated, 
      user: authState.user, 
      loading: authState.loading,
      firebaseUser: authState.firebaseUser,
      loginWithFirebaseGoogle,
      logout,
      updateUserProfile,
      getFirebaseToken
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;